// TimeBarSection.js
// ─────────────────────────────────────────────────────────────────────────────
// NOTICE / TIME-BAR ENGINE  (GCC 2015)
//
// The single highest-value alert in contract administration: it warns you
// BEFORE a deadline expires. Each entry has a trigger event + date and a period
// (days); the engine computes the due date and the days remaining, and raises
// the alarm as the bar approaches — and flags it the moment it is breached.
//
// Built-in clause presets (editable period per entry):
//   • Claim notice (GCC 10.1.1)        — 28 days from the circumstance
//   • Claim particulars (GCC 10.1.5)   — 28 days from the notice
//   • Dissatisfaction / dispute (10.5) — 28 days
//   • Payment due (GCC 6.10.5)         — 28 days from the payment certificate;
//     once overdue, an interest-on-late-payment flag is raised (GCC 6.10.6).
//   • Custom                            — set your own period.
//
// Status is computed, never just stored:
//   Submitted/Closed → done.  Else if today > due → EXPIRED (time-barred).
//   Else days-remaining; ≤ the warning window → "Due soon".
//
// Firestore: projects/{projectId}/timeBars — one doc per notice/deadline.
// Self-contained: reads/writes its own collection, takes only the project.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo } from "react";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";

const C = tokens.color;
const MONO = tokens.font?.mono || '"JetBrains Mono", monospace';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const numberOrZero = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const todayISO = () => new Date().toISOString().slice(0, 10);
const DAY = 86400000;
const WARN_WINDOW = 7; // days — "Due soon" threshold

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
};

function addDays(iso, days) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return "";
  d.setDate(d.getDate() + numberOrZero(days));
  return d.toISOString().slice(0, 10);
}

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return null;
  const t = new Date(todayISO() + "T00:00:00");
  return Math.round((d - t) / DAY);
};

// Clause presets: label → { type, days, interest }
const PRESETS = {
  "Claim notice (GCC 10.1.1)":        { days: 28, interest: false },
  "Claim particulars (GCC 10.1.5)":   { days: 28, interest: false },
  "Dissatisfaction / dispute (10.5)": { days: 28, interest: false },
  "Payment due (GCC 6.10.5)":         { days: 28, interest: true },
  "Custom":                            { days: 14, interest: false },
};
const PRESET_KEYS = Object.keys(PRESETS);

const OPEN_STATUSES = ["Open", "Submitted", "Closed"];

// Computed state of an entry. dueDate = triggerDate + periodDays.
function computeState(entry) {
  const due = addDays(entry.triggerDate, entry.periodDays);
  const remaining = daysUntil(due);
  let state;
  if (entry.status === "Closed") state = "Closed";
  else if (entry.status === "Submitted") state = "Submitted";
  else if (remaining === null) state = "No date";
  else if (remaining < 0) state = "EXPIRED";
  else if (remaining <= WARN_WINDOW) state = "Due soon";
  else state = "Open";
  // interest flag: payment-type, past due, not submitted/closed
  const interestDue = !!entry.interest && remaining !== null && remaining < 0 && entry.status === "Open";
  return { due, remaining, state, interestDue };
}

const STATE_META = {
  "EXPIRED":   { bg: C.dangerSoft,  text: C.danger,  border: C.dangerBorder },
  "Due soon":  { bg: C.warningSoft, text: C.warning, border: C.warningBorder },
  "Open":      { bg: C.infoSoft,    text: C.info,    border: C.infoBorder },
  "Submitted": { bg: C.successSoft, text: C.success, border: C.successBorder },
  "Closed":    { bg: C.neutralSoft, text: C.neutral, border: C.neutralBorder },
  "No date":   { bg: C.neutralSoft, text: C.neutral, border: C.neutralBorder },
};

// urgency rank for sorting (lower = more urgent)
function urgencyRank(st) {
  if (st.state === "EXPIRED") return st.interestDue ? -1 : 0;
  if (st.state === "Due soon") return 1;
  if (st.state === "Open") return 2 + Math.min(st.remaining ?? 9999, 9000) / 10000;
  if (st.state === "Submitted") return 100;
  if (st.state === "Closed") return 200;
  return 300;
}

// ─── Shared styles ───────────────────────────────────────────────────────────

const s = {
  page: { display: "grid", gap: 20, minWidth: 0, fontFamily: tokens.font?.body || '"Inter", sans-serif', color: C.ink },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, boxShadow: tokens.shadow?.sm, minWidth: 0 },
  title: { margin: 0, fontSize: 24, fontFamily: tokens.font?.display, color: C.ink, letterSpacing: "-0.02em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: C.inkMuted },
  h3: { margin: "0 0 12px", fontSize: 13, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" },
  lbl: { display: "block", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, marginBottom: 4 },
  inp: { width: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 12.5, color: C.ink, fontFamily: "inherit", outline: "none" },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnAccent: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnSmall: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  btnDanger: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.dangerBorder}`, background: C.dangerSoft, color: C.danger, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  th: { padding: "8px 10px", textAlign: "left", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `2px solid ${C.border}`, background: C.surfaceAlt, whiteSpace: "nowrap" },
  td: { padding: "8px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, verticalAlign: "top" },
  chip: (m) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.03em", background: m.bg, color: m.text, border: `1px solid ${m.border}`, whiteSpace: "nowrap" }),
  mono: { fontFamily: MONO, fontVariantNumeric: "tabular-nums" },
  statTile: { background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", minWidth: 0 },
  statLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, margin: 0 },
  statValue: { fontSize: 20, fontWeight: 800, fontFamily: MONO, color: C.ink, margin: "2px 0 0" },
};

const blankForm = () => ({
  type: "Claim notice (GCC 10.1.1)", ref: "", description: "", relatedRef: "",
  triggerEvent: "", triggerDate: todayISO(), periodDays: 28,
  status: "Open", submittedDate: "", interest: false,
});

function nextRef(items) {
  const nums = items.map((e) => parseInt((e.ref || "").replace(/[^0-9]/g, ""), 10)).filter(Number.isFinite);
  return `TB-${String(nums.length ? Math.max(...nums) + 1 : 1).padStart(3, "0")}`;
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════════════════════════
function TimeBarSection({ currentProject, currentProjectId }) {
  const projectId = currentProject?.id || currentProjectId;

  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(blankForm);
  const [editingId, setEditingId] = useState(null);
  const [showClosed, setShowClosed] = useState(false);

  useEffect(() => {
    if (!projectId) { setEntries([]); return undefined; }
    const q = query(collection(db, "projects", projectId, "timeBars"), orderBy("triggerDate"));
    const unsub = onSnapshot(q,
      (snap) => setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("timeBars listener:", err)
    );
    return unsub;
  }, [projectId]);

  const rows = useMemo(() => {
    const withState = entries.map((e) => ({ ...e, _st: computeState(e) }));
    const visible = showClosed ? withState : withState.filter((e) => e._st.state !== "Closed");
    return visible.sort((a, b) => urgencyRank(a._st) - urgencyRank(b._st));
  }, [entries, showClosed]);

  const kpis = useMemo(() => {
    let expired = 0, dueSoon = 0, open = 0, interest = 0;
    entries.forEach((e) => {
      const st = computeState(e);
      if (st.state === "EXPIRED") expired += 1;
      else if (st.state === "Due soon") dueSoon += 1;
      else if (st.state === "Open") open += 1;
      if (st.interestDue) interest += 1;
    });
    return { expired, dueSoon, open, interest };
  }, [entries]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function applyPreset(type) {
    const p = PRESETS[type] || PRESETS.Custom;
    setForm((f) => ({ ...f, type, periodDays: p.days, interest: p.interest }));
  }

  function resetForm() { setForm(blankForm()); setEditingId(null); }

  async function saveEntry() {
    if (!projectId) return;
    if (!form.triggerDate) { alert("A trigger date is required to compute the deadline."); return; }
    const ref = (form.ref || "").trim() || nextRef(entries);
    const payload = {
      ...form, ref, periodDays: numberOrZero(form.periodDays),
      submittedDate: form.status === "Open" ? "" : (form.submittedDate || todayISO()),
      updatedAt: serverTimestamp(),
    };
    try {
      if (editingId) await updateDoc(doc(db, "projects", projectId, "timeBars", editingId), payload);
      else await addDoc(collection(db, "projects", projectId, "timeBars"), { ...payload, createdAt: serverTimestamp() });
      resetForm();
    } catch (e) { alert(`Save failed: ${e.message || e}`); }
  }

  function editEntry(e) {
    setForm({
      type: e.type || "Custom", ref: e.ref || "", description: e.description || "",
      relatedRef: e.relatedRef || "", triggerEvent: e.triggerEvent || "",
      triggerDate: e.triggerDate || todayISO(), periodDays: numberOrZero(e.periodDays) || 28,
      status: e.status || "Open", submittedDate: e.submittedDate || "", interest: !!e.interest,
    });
    setEditingId(e.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function markSubmitted(e) {
    if (!projectId) return;
    try {
      await updateDoc(doc(db, "projects", projectId, "timeBars", e.id), {
        status: "Submitted", submittedDate: e.submittedDate || todayISO(), updatedAt: serverTimestamp(),
      });
    } catch (err) { alert(`Update failed: ${err.message || err}`); }
  }

  async function removeEntry(id) {
    if (!projectId) return;
    if (!window.confirm("Delete this deadline?")) return;
    try { await deleteDoc(doc(db, "projects", projectId, "timeBars", id)); }
    catch (e) { alert(`Delete failed: ${e.message || e}`); }
  }

  if (!projectId) {
    return <div style={s.page}><div style={s.card}><p style={s.sub}>Select a project to view its notice & time-bar register.</p></div></div>;
  }

  const previewDue = addDays(form.triggerDate, form.periodDays);

  return (
    <div style={s.page}>
      <div style={s.card}>
        <h1 style={s.title}>Notice & Time-Bar Engine</h1>
        <p style={s.sub}>Tracks every contractual deadline — GCC 10.1 claim notices, dispute windows and payment-due dates — and warns you before the bar expires. Expired bars and late-payment interest are flagged in red.</p>
      </div>

      {/* ── alert KPIs ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <div style={{ ...s.statTile, ...(kpis.expired ? { borderColor: C.dangerBorder, background: C.dangerSoft } : null) }}>
          <p style={s.statLabel}>Expired / time-barred</p><p style={{ ...s.statValue, color: kpis.expired ? C.danger : C.ink }}>{kpis.expired}</p>
        </div>
        <div style={{ ...s.statTile, ...(kpis.dueSoon ? { borderColor: C.warningBorder, background: C.warningSoft } : null) }}>
          <p style={s.statLabel}>Due within {WARN_WINDOW} days</p><p style={{ ...s.statValue, color: kpis.dueSoon ? C.warning : C.ink }}>{kpis.dueSoon}</p>
        </div>
        <div style={s.statTile}><p style={s.statLabel}>Open</p><p style={s.statValue}>{kpis.open}</p></div>
        <div style={{ ...s.statTile, ...(kpis.interest ? { borderColor: C.dangerBorder } : null) }}>
          <p style={s.statLabel}>Payments accruing interest</p><p style={{ ...s.statValue, color: kpis.interest ? C.danger : C.ink }}>{kpis.interest}</p>
        </div>
      </div>

      {/* ── add / edit ── */}
      <div style={s.card}>
        <h3 style={s.h3}>{editingId ? "Edit deadline" : "Add deadline / notice"}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <div><label style={s.lbl}>Type</label>
            <select style={s.inp} value={form.type} onChange={(e) => applyPreset(e.target.value)}>
              {PRESET_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div><label style={s.lbl}>Ref</label><input style={s.inp} value={form.ref} onChange={(e) => setField("ref", e.target.value)} placeholder="auto (TB-001)" /></div>
          <div style={{ gridColumn: "span 2" }}><label style={s.lbl}>Trigger event</label><input style={s.inp} value={form.triggerEvent} onChange={(e) => setField("triggerEvent", e.target.value)} placeholder="Unforeseen ground conditions encountered at Ch 1+200" /></div>
          <div><label style={s.lbl}>Trigger date</label><input type="date" style={s.inp} value={form.triggerDate} onChange={(e) => setField("triggerDate", e.target.value)} /></div>
          <div><label style={s.lbl}>Period (days)</label><input type="number" style={s.inp} value={form.periodDays} onChange={(e) => setField("periodDays", e.target.value)} /></div>
          <div><label style={s.lbl}>Computed due date</label><input style={{ ...s.inp, ...s.mono, background: C.surfaceAlt }} value={fmtDate(previewDue)} readOnly /></div>
          <div><label style={s.lbl}>Related ref</label><input style={s.inp} value={form.relatedRef} onChange={(e) => setField("relatedRef", e.target.value)} placeholder="Claim CL-03 / Cert 12" /></div>
          <div><label style={s.lbl}>Status</label><select style={s.inp} value={form.status} onChange={(e) => setField("status", e.target.value)}>{OPEN_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <label style={{ ...s.lbl, marginBottom: 0, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={form.interest} onChange={(e) => setField("interest", e.target.checked)} />
              Interest on late payment
            </label>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button style={s.btnAccent} onClick={saveEntry}>{editingId ? "Update" : "Add deadline"}</button>
          {editingId && <button style={s.btn} onClick={resetForm}>Cancel</button>}
        </div>
      </div>

      {/* ── register, sorted by urgency ── */}
      <div style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ ...s.h3, margin: 0 }}>Deadlines — most urgent first</h3>
          <label style={{ ...s.sub, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> show closed
          </label>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Ref", "Type", "Trigger event", "Trigger", "Days", "Due", "Remaining", "Status", "Actions"].map((h) => <th key={h} style={s.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((e) => {
                const st = e._st;
                const meta = STATE_META[st.state] || STATE_META.Open;
                const remainingTxt = st.remaining === null ? "—"
                  : st.remaining < 0 ? `${Math.abs(st.remaining)}d overdue`
                  : `${st.remaining}d`;
                return (
                  <tr key={e.id} style={st.state === "EXPIRED" ? { background: C.dangerSoft } : null}>
                    <td style={{ ...s.td, ...s.mono, fontWeight: 700 }}>{e.ref || "—"}</td>
                    <td style={s.td}>{e.type || "—"}</td>
                    <td style={s.td}>{e.triggerEvent || e.description || "—"}{e.relatedRef ? <span style={{ ...s.sub, display: "block" }}>↳ {e.relatedRef}</span> : null}</td>
                    <td style={{ ...s.td, ...s.mono }}>{fmtDate(e.triggerDate)}</td>
                    <td style={{ ...s.td, ...s.mono }}>{numberOrZero(e.periodDays)}</td>
                    <td style={{ ...s.td, ...s.mono, fontWeight: 700 }}>{fmtDate(st.due)}</td>
                    <td style={{ ...s.td, ...s.mono, color: st.remaining !== null && st.remaining < 0 ? C.danger : st.remaining !== null && st.remaining <= WARN_WINDOW ? C.warning : C.ink, fontWeight: 700 }}>{remainingTxt}</td>
                    <td style={s.td}>
                      <span style={s.chip(meta)}>{st.state}</span>
                      {st.interestDue && <span style={{ ...s.chip(STATE_META.EXPIRED), marginLeft: 4 }}>interest</span>}
                    </td>
                    <td style={s.td}>
                      <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                        {e.status === "Open" && <button style={s.btnSmall} onClick={() => markSubmitted(e)}>Mark submitted</button>}
                        <button style={s.btnSmall} onClick={() => editEntry(e)}>Edit</button>
                        <button style={s.btnDanger} onClick={() => removeEntry(e.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!rows.length && <tr><td style={{ ...s.td, color: C.inkMuted }} colSpan={9}>No deadlines tracked{showClosed ? "" : " (closed hidden)"}.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default React.memo(TimeBarSection);
